/**
 * NOTE: Mobile Auth Mock Service - Development Only
 * 
 * This service is ONLY used for rapid UI development in web browser.
 * It is NEVER used in production or on native devices.
 * 
 * Security safeguards in mobile-auth.module.ts ensure it only loads when:
 * - environment.production === false
 * - environment.appName === ClientNames.mobile
 * - Capacitor.isNativePlatform() === false (web browser only)
 * 
 * All user data is configurable via localStorage (no hardcoded personal data):
 * - SD_DEV_TOKEN: Mock authentication token
 * - SD_DEV_USER_ID: User ID (defaults to auto-generated)
 * - SD_DEV_USER_NAME: Display name (defaults to "Development User")
 * - SD_DEV_USER_EMAIL: Email address (defaults to "dev@example.com")
 * 
 * Use window.sdAuthDebug.getStatus() for available commands.
 */
import { Injectable, NgZone } from '@angular/core';
import { User as Auth0User } from '@auth0/auth0-spa-js';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AppState } from 'src/app/reducers';
import { LogIn, LogOut } from 'src/app/auth/store/auth.actions';
import { AuthUser } from 'src/app/auth/models/auth-user.model';
import { tap, delay } from 'rxjs/operators';

interface WindowWithDebug extends Window {
  sdAuthDebug: {
    setToken: (token: string, email?: string) => void;
    clearToken: () => void;
    getStatus: () => void;
  };
}

declare const window: WindowWithDebug;

@Injectable()
export class MobileAuthMockService {
  private isLoggingOut = false;
  private mockUser: Auth0User;
  private mockToken: string;

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly auth0Service: AuthService
  ) {
    this.clearOldPreferences();
    this.initializeMockData();
    this.setupDebugHelpers();
  }

  private clearOldPreferences(): void {
    const keysToRemove = [
      'biometric-enabled',
      'device-logged-in',
      'logout-state',
      'open-id',
    ];

    let hadOldData = false;
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        hadOldData = true;
      }
    });

    if (hadOldData) {
      setTimeout(() => location.reload(), 100);
    }
  }

  private initializeMockData(): void {
    const storedToken = localStorage.getItem('SD_DEV_TOKEN');
    const storedUserId = localStorage.getItem('SD_DEV_USER_ID');
    const storedUserName = localStorage.getItem('SD_DEV_USER_NAME');
    const storedEmail = localStorage.getItem('SD_DEV_USER_EMAIL');

    const mockUserId = storedUserId || 'auth0|mock-dev-user-' + Date.now();
    const mockUserName = storedUserName || 'Development User';
    const mockEmail = storedEmail || 'dev@example.com';

    this.mockToken = storedToken || 'mock-token-' + Date.now();
    this.mockUser = {
      sub: mockUserId,
      name: mockUserName,
      email: mockEmail,
      email_verified: true,
      updated_at: new Date().toISOString(),
    };
  }

  private setupDebugHelpers(): void {
    if (typeof window !== 'undefined') {
      window.sdAuthDebug = {
        setToken: (token: string, email?: string) => {
          localStorage.setItem('SD_DEV_TOKEN', token);
          if (email) {
            localStorage.setItem('SD_DEV_USER_EMAIL', email);
          }
          console.log(
            '[sdAuthDebug] ✅ Token saved. Reload page to apply changes.'
          );
          console.log('[sdAuthDebug] 💡 Run: location.reload()');
        },
        clearToken: () => {
          localStorage.removeItem('SD_DEV_TOKEN');
          localStorage.removeItem('SD_DEV_USER_ID');
          localStorage.removeItem('SD_DEV_USER_NAME');
          localStorage.removeItem('SD_DEV_USER_EMAIL');
          console.log(
            '[sdAuthDebug] ✅ Configuration cleared. Reload page to use default mock data.'
          );
          console.log('[sdAuthDebug] 💡 Run: location.reload()');
        },
        getStatus: () => {
          const hasToken = !!localStorage.getItem('SD_DEV_TOKEN');
          const userId = localStorage.getItem('SD_DEV_USER_ID');
          const userName = localStorage.getItem('SD_DEV_USER_NAME');
          const email = localStorage.getItem('SD_DEV_USER_EMAIL');
          
          console.log('[sdAuthDebug] 📊 Current status:');
          console.log('  - Using real token:', hasToken);
          console.log('  - User ID:', userId || 'auth0|mock-dev-user-* (auto-generated)');
          console.log('  - User Name:', userName || 'Development User (default)');
          console.log('  - Email:', email || 'dev@example.com (default)');
          console.log(
            '  - Token:',
            hasToken ? 'Configured ✅' : 'Not configured ❌'
          );
          console.log('\n💡 Available commands:');
          console.log('  window.sdAuthDebug.setToken("your-token", "email@example.com")');
          console.log('  window.sdAuthDebug.clearToken()');
          console.log('  window.sdAuthDebug.getStatus()');
          console.log('\n💡 Manual configuration via localStorage:');
          console.log('  localStorage.setItem("SD_DEV_TOKEN", "your-token")');
          console.log('  localStorage.setItem("SD_DEV_USER_ID", "auth0|your-user-id")');
          console.log('  localStorage.setItem("SD_DEV_USER_NAME", "Your Name")');
          console.log('  localStorage.setItem("SD_DEV_USER_EMAIL", "your@email.com")');
        },
      };
    }
  }

  loginWithRedirect(): Observable<void> {
    return of(void 0).pipe(
      delay(300),
      tap(() => {
        this.zone.run(() => {
          this.store.dispatch(
            new LogIn({
              token: this.mockToken,
              user: AuthUser.fromAuth0(this.mockUser),
            })
          );

          this.router.navigate(['/core/home/']).catch(error => {
            console.error('[MobileAuthMockService] Navigation error:', error);
          });
        });
      })
    );
  }

  logOut(): Observable<void> {
    if (this.isLoggingOut) {
      return of(void 0);
    }

    this.isLoggingOut = true;

    return of(void 0).pipe(
      delay(300),
      tap(() => {
        this.zone.run(() => {
          this.store.dispatch(new LogOut());
          this.isLoggingOut = false;
          setTimeout(() => location.reload(), 500);
        });
      })
    );
  }

  getAuth0User(): Observable<Auth0User> {
    return of(this.mockUser);
  }

  getTokenSilently(): Observable<string> {
    return of(this.mockToken);
  }

  getIsLoggingOut(): boolean {
    return this.isLoggingOut;
  }
}
